import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

export interface User {
  _id?: string;
  username: string;
  email?: string;
  role: 'admin' | 'user';
  firstName?: string;
  lastName?: string;
  createdAt?: Date;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: User;
  token?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;
  private tokenSubject: BehaviorSubject<string | null>;

  // Mock users
  private mockUsers: User[] = [
    {
      _id: '1',
      username: 'admin',
      email: 'admin@aadinathentp.com',
      role: 'admin',
      firstName: 'Admin',
      lastName: 'User'
    }
  ];

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    let currentUser: User | null = null;
    let token: string | null = null;

    if (isPlatformBrowser(this.platformId)) {
      const userData = localStorage.getItem('currentUser');
      const storedToken = localStorage.getItem('token');
      
      if (userData) {
        try {
          currentUser = JSON.parse(userData);
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
      
      if (storedToken) {
        token = storedToken;
      }
    }

    this.currentUserSubject = new BehaviorSubject<User | null>(currentUser);
    this.currentUser = this.currentUserSubject.asObservable();
    this.tokenSubject = new BehaviorSubject<string | null>(token);
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  public get tokenValue(): string | null {
    return this.tokenSubject.value;
  }

  login(username: string, password: string): Observable<AuthResponse> {
    // Mock login logic
    const user = this.mockUsers.find(u => u.username === username);
    
    if (user && password === 'admin123') {
      const token = 'mock-jwt-token-' + Date.now();
      const response: AuthResponse = {
        success: true,
        message: 'Login successful',
        user: user,
        token: token
      };

      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem('currentUser', JSON.stringify(user));
        localStorage.setItem('token', token);
      }

      this.currentUserSubject.next(user);
      this.tokenSubject.next(token);

      return of(response).pipe(delay(1000));
    } else {
      return of({
        success: false,
        message: 'Invalid credentials'
      }).pipe(delay(1000));
    }
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('token');
    }

    this.currentUserSubject.next(null);
    this.tokenSubject.next(null);
    this.router.navigate(['/']);
  }

  isAuthenticated(): boolean {
    return !!this.currentUserValue && !!this.tokenValue;
  }

  isAdmin(): boolean {
    return this.isAuthenticated() && this.currentUserValue?.role === 'admin';
  }
}
